<template>
  <div class="mb-4 w-full rounded-lg border-2 p-4" :class="colorClasses.border">
    <p class="mb-4 text-center font-medium" :class="colorClasses.text">
      {{ confirmText }}
    </p>
    <div class="flex items-center justify-between gap-4">
      <button
        class="flex-1 rounded-lg border-2 border-gray-400 bg-white px-4 py-2 font-medium text-gray-700 shadow-sm transition-all hover:shadow-none active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-kekkle-dark-light dark:text-slate-200"
        :disabled="loading"
        @click="$emit('cancel')"
      >
        {{ cancelText }}
      </button>
      <button
        class="relative flex-1 rounded-lg border-2 border-black-dark px-4 py-2 font-medium text-white transition-all hover:shadow-none active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
        :class="colorClasses.button"
        :disabled="!canConfirm || loading"
        @click="$emit('confirm')"
      >
        <div
          v-if="loading"
          class="absolute left-0 top-0 flex h-full w-full items-center justify-center"
        >
          <div
            class="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"
          ></div>
        </div>
        <span :class="{ invisible: loading }">{{ deleteText }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";

type Color = "red" | "purple";

type Props = {
  confirmText: string;
  cancelText: string;
  deleteText: string;
  loading?: boolean;
  color?: Color;
  delayMs?: number;
};

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  color: "red",
  delayMs: 2000,
});

defineEmits<{
  (e: "cancel"): void;
  (e: "confirm"): void;
}>();

const canConfirm = ref(false);
const mounted = ref(false);

const colorClasses = computed(() => {
  if (props.color === "purple") {
    return {
      border: "border-kekkle-purple-dark bg-kekkle-purple-light",
      text: "text-kekkle-purple-dark",
      button: "bg-kekkle-purple shadow-purple-hard-sm",
    };
  }
  // Default red
  return {
    border: "border-kekkle-red-dark bg-kekkle-red-light",
    text: "text-kekkle-red-dark",
    button: "bg-kekkle-red shadow-red-hard-sm",
  };
});

// Watch for when component is mounted to start the delay timer
watch(
  () => mounted.value,
  (isMounted) => {
    if (isMounted) {
      canConfirm.value = false;
      setTimeout(() => {
        canConfirm.value = true;
      }, props.delayMs);
    }
  },
  { immediate: true },
);

// Set mounted flag
mounted.value = true;
</script>
